<template>
    <div id="reset_password">
      <!-- Reset Password Form -->
      <form @submit.prevent="submitResetPasswordForm">
        <div class="bg-white mb-2">
          <div class="form-shadow">
            <div class="row">
              <div class="col-lg-12" id="new-password-section">
                <!-- Label and New Password PIN Fields -->
                <label for="newPassword" class="form-label">New Password</label>
                <div class="otp-number">
                  <div v-for="index in 4" :key="'new-' + index" class="form-group">
                    <input
                      id="newPassword"
                      type="tel"
                      class="form-control rounded-0 otp-control"
                      v-model="newPassword[index - 1]"
                      placeholder=""
                      maxlength="1"
                      @keydown="handleKeyDown('new', index - 1, $event)"
                      data-field="new"
                      aria-label="New Password"
                    />
                  </div>
                </div>
              </div>
  
              <div class="col-lg-12" id="confirm-password-section">
                <!-- Label and Confirm Password PIN Fields -->
                <label for="confirmPassword" class="form-label">Confirm Password</label>
                <div class="otp-number">
                  <div v-for="index in 4" :key="'confirm-' + index" class="form-group">
                    <input
                      id="confirmPassword"
                      type="tel"
                      class="form-control rounded-0 otp-control"
                      v-model="confirmPassword[index - 1]"
                      placeholder=""
                      maxlength="1"
                      @keydown="handleKeyDown('confirm', index - 1, $event)"
                      data-field="confirm"
                      aria-label="Confirm Password"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <!-- Submit Button -->
        <div class="text-center mb-4">
          <button :disabled="isDisable" type="submit" class="btn btn-primary fw-600 rounded-0 reg-btn">
            RESET PASSWORD
          </button>
        </div>
      </form>
  
      <!-- Back to Login -->
      <div class="already-text">
        <p>Back to?<a href="/front/login"><span> Login</span></a></p>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, nextTick } from 'vue';
  import { useForm } from 'vee-validate';
  import * as yup from 'yup';
  import axios from 'axios';
  
  // Define state variables
  const errorMessages = ref({});
  const newPassword = ref(['', '', '', '']);
  const confirmPassword = ref(['', '', '', '']);
  const isDisable = ref(false);
  
  // Validation schema for 4-digit PINs (or more if necessary)
  const validationSchema = yup.object({
    newPassword: yup.string().required('New password is required').min(6, 'Password must be at least 6 characters'),
    confirmPassword: yup
      .string()
      .required('Confirm password is required')
      .oneOf([yup.ref('newPassword'), null], 'Passwords must match'),
  });
  
  // Form handler with validation
  const { handleSubmit, errors, resetForm, validate } = useForm({
    validationSchema,
  });
  
  // Handle key down events for OTP-like behavior
  const handleKeyDown = (field, index, event) => {
    if (field === 'new' || field === 'confirm') {
      if (event.keyCode === 8 && (index === 0 || event.target.value === '')) {
        // If backspace is pressed and it's the first input, allow moving back
        if (index > 0) {
          nextTick(() => {
            const inputs = document.querySelectorAll(`input[data-field="${field}"]`);
            inputs[index - 1].focus();
          });
        }
      } else if (event.keyCode >= 48 && event.keyCode <= 57 && event.target.value.length === 1) {
        // Automatically focus the next input if a digit is entered
        if (index < 3) {
          nextTick(() => {
            const inputs = document.querySelectorAll(`input[data-field="${field}"]`);
            inputs[index + 1].focus();
          });
        }
      }
    }
  };

  const csrfToken = document.head.querySelector('meta[name="csrf-token"]').content;

  const getQueryParam = (param) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  };

  const mobile = getQueryParam('mobile');
  
  // Submit form for resetting password
  const submitResetPasswordForm = async () => {
    isDisable.value = true; 
    const isValid = await validate(); // Validate the form manually
  
    if (!isValid) {
      isDisable.value = false; // Re-enable the button if validation fails
      return;
    }
  
    try {
      // Join the digits of the newPassword and confirmPassword
      const newPass = newPassword.value.join('');
      const confirmPass = confirmPassword.value.join('');
  
      // Send POST request to reset password
      const response = await axios.post('/front/auth/reset-password', {
        password: newPass,
        password_confirmation: confirmPass,
        mobile: mobile,
        _token: csrfToken,
      });
  
      // Success message and redirect
      window.successSnackbar('Password reset successfully');
      setTimeout(function () {
        window.location.href = '/front/login';
      }, 2000);
    } catch (error) {
      // Handle errors
      console.error(error);
      window.errorSnackbar('An error occurred while resetting the password');
    } finally {
      isDisable.value = false; // Re-enable the button after the operation
    }
  };
  </script>
  
  <style scoped>
  /* Add any additional styles here if needed */
  </style>
  